import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import nookies from 'nookies';

import { InitRequest } from '@/hooks/store';
import { determineComplianceLevelMiddleware } from '@/server/middleware/determineComplianceLevel';
import { initStateMiddleware } from '@/server/middleware/initStateMiddleware';
import { InitialServerState } from '@/store';
import { isLocale } from '@/store/locale/types';
import { initialState as defaultPrices } from '@/store/prices/reducers';
import { RouteSpecificPurchaseParams } from '@/store/purchaseParams/types';
import {
	getPagesRouterSsrDirectOpts,
	type PagesRouterSsrDirectOpts,
} from '@/utils/experiments/getPagesRouterSsrDirectOpts';
import { getBrowserLanguageFromHeaders } from '@/utils/getBrowserLanguageFromHeaders';
import { initPurchaseParams } from '@/utils/getInitialState';
import { getCalmLanguage } from '@/utils/language';
import { allowsEvaluationDateOverride } from '@/utils/promoOffer/allowsEvaluationDateOverride';
import { buildSurface } from '@/utils/promoOffer/buildSurface';

interface GetInitialServerState {
	(
		context: GetServerSidePropsContext,
		requests?: InitRequest[],
		routeSpecificPurchaseParams?: RouteSpecificPurchaseParams | null,
		// Pass a resolved PagesRouterSsrDirectOpts when the caller already evaluated the gate
		// for its own fetches (e.g. alongside a getPartner call) — reusing it avoids a second
		// gate evaluation on the same request. getPagesRouterSsrDirectOpts returns undefined
		// both when the switch is OFF and on its internal fail-safe, so a caller that already
		// evaluated must pass 'skip-gate' rather than that undefined result, or an OFF/failed
		// evaluation would be indistinguishable from "not evaluated yet" and get redone here.
		// 'skip-gate' also covers the webview wrapper's opt-out — its direct path's Bearer-only
		// auth can't consume the header-borne webview session token, so evaluating the gate
		// would only risk silently SSR-ing a webview page as logged out. Omit the param entirely
		// and the gate is evaluated here, so every other Pages-Router page routes direct without
		// opting in per page.
		ssrDirectOpts?: PagesRouterSsrDirectOpts | 'skip-gate',
		// Opt-in for the promo-offer fetch — only the marketing pages that currently carry
		// the banner pass true. When set, the surface is derived from the request's own
		// resolved URL (pathname + query string) rather than being passed in separately.
		withPromoOffer?: boolean,
	): Promise<InitialServerState | null>;
}

export const getInitialServerState: GetInitialServerState = async (
	context,
	requests = [InitRequest.DEVICE, InitRequest.USER, InitRequest.PRICES],
	routeSpecificPurchaseParams = {},
	ssrDirectOpts,
	withPromoOffer = false,
) => {
	const { req, res, query, locale } = context;
	const cookies = nookies.get({ req });
	const headers = req.headers;

	// Use browser language detection for locale
	const browserLanguage = getBrowserLanguageFromHeaders(req.headers);
	const detectedLocale = getCalmLanguage(browserLanguage);

	// Device id is guaranteed to be set in Express - either as a cookie or header
	const deviceId = cookies['x-device-id'] || (req.headers['x-device-id'] as string);

	const couponForPriceCall = routeSpecificPurchaseParams?.coupon ?? query?.coupon;

	// evaluation_date lets a non-production caller pin "now" for the promo-offer window
	// without a real clock skew; production never honors it, whatever the query says.
	const evaluationDateParam = Array.isArray(query?.evaluation_date)
		? query.evaluation_date[0]
		: query?.evaluation_date;
	// resolvedUrl carries pathname + query as a single already-encoded string; split off the
	// query so buildSurface can re-serialize it the same way loadInitialState and the client
	// refetch do, rather than trusting Next's own encoding of it.
	const promoOfferSurface = withPromoOffer
		? buildSurface(context.resolvedUrl.split('?')[0], query)
		: undefined;
	const evaluationDate = withPromoOffer && allowsEvaluationDateOverride() ? evaluationDateParam : undefined;

	// Mirrors what loadInitialState does for the App Router, so the two routers agree on which
	// transport serves SSR. 'skip-gate' (used by the webview wrapper) bypasses evaluation
	// entirely; otherwise getPagesRouterSsrDirectOpts returns undefined whenever the
	// kill-switch is OFF or its evaluation fails, which keeps the authproxy as the fail-safe.
	const resolvedSsrDirectOpts =
		ssrDirectOpts === 'skip-gate'
			? undefined
			: (ssrDirectOpts ?? (await getPagesRouterSsrDirectOpts(context)));

	const { device, user, prices, requiresGdpr, program, guide, promoOffer } = await initStateMiddleware(
		req,
		res,
		cookies,
		headers,
		deviceId,
		query,
		requests,
		couponForPriceCall,
		promoOfferSurface,
		evaluationDate,
		resolvedSsrDirectOpts,
	);
	const complianceLevel = determineComplianceLevelMiddleware(req, requiresGdpr);
	const purchaseParams = initPurchaseParams({
		query,
		prices,
		user,
		routeSpecificPurchaseParams,
	});

	return {
		browserLanguage: detectedLocale,
		complianceLevel,
		...(device ? { device } : {}),
		prices: prices ?? defaultPrices,
		purchaseParams,
		user,
		appLoaded: true,
		serverSide: true,
		locale: device?.language ?? (isLocale(locale) ? locale : 'en'),
		program,
		guide,
		promoOffer,
		promoOfferEnabled: withPromoOffer,
	};
};

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);

	return {
		props: {
			initialState,
		},
	};
};
